import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useCountUp } from 'react-countup';
import { toShortNumFormat } from '@common/lib/toShortNumFormat';
import mergeRefs from 'merge-refs';

import { safeCalculateMindedResources } from '@/common/lib/safeCalculateMindedResources';
import { ClaimAnimationContext } from '@/context/ClaimAnimationContext';
import { selectBoostForQuarry } from '@/redux/selectors/quarrySelectors';
import { useAppSelector } from '@/redux/store';

interface StockProps {
  plantId?: string;
  stock: number;
  limit: number;
  productionRate: number;
  updatedAt?: string;
  localStockUpdatedAt?: string;
  withoutFormat?: boolean;
}

const Stock = forwardRef<HTMLDivElement, StockProps>(
  (
    {
      plantId,
      stock,
      limit,
      productionRate,
      localStockUpdatedAt,
      withoutFormat = false,
    },
    ref,
  ) => {
    const countUpRef = useRef<HTMLDivElement | null>(null);
    const { isStockAnimationStart } = useContext(ClaimAnimationContext);
    const [countStock, setCountStock] = useState<{
      start: number;
      end: number;
    } | null>(null);
    const formattingFn = useCallback(
      (value: number): string => {
        return withoutFormat ? String(value) : String(toShortNumFormat(value));
      },
      [withoutFormat],
    );
    const boosts = useAppSelector((state) =>
      selectBoostForQuarry(state, plantId),
    );
    const updateStockCounter = useCallback(
      ({
        stock,
        productionRate,
        limit,
        withReset,
      }: {
        stock: number;
        limit: number;
        productionRate: number;
        withReset?: boolean;
      }): void => {
        const calcProducted = (
          updatedAt: Date,
          timePoint?: Date,
          startPoint?: Date,
        ): number => {
          const plant = {
            updatedAt,
            productionRate,
            boosts: boosts || [],
            timePoint,
            startPoint,
          };

          return safeCalculateMindedResources(plant);
        };

        setCountStock((state) => {
          const dateNow = new Date();
          const nextTimePoint = new Date(dateNow.getTime() + 60 * 1000);
          const updatedDate = localStockUpdatedAt
            ? new Date(localStockUpdatedAt)
            : dateNow;
          const clockBehindServer = dateNow < updatedDate;
          const safeStartPoint = clockBehindServer ? updatedDate : dateNow;
          const safeNextTimePoint =
            nextTimePoint < safeStartPoint ? safeStartPoint : nextTimePoint;

          const productedFromNowToNextTimePoint = calcProducted(
            updatedDate,
            safeNextTimePoint,
            safeStartPoint,
          );
          const productedFromUpdatedAtToNow = clockBehindServer
            ? 0
            : calcProducted(updatedDate);
          const start = stock + productedFromUpdatedAtToNow;
          const end = state
            ? state.end + productedFromNowToNextTimePoint
            : start + productedFromNowToNextTimePoint;

          return {
            start: withReset ? start : state ? state.end : start,
            end: end > limit ? limit : end,
          };
        });
      },
      [boosts, localStockUpdatedAt, stock],
    );
    useCountUp({
      ref: countUpRef,
      useEasing: false,
      end: countStock?.end || 0,
      start: countStock?.start || 0,
      separator: '.',
      duration: 60,
      onEnd: () => updateStockCounter({ stock, limit, productionRate }),
      onUpdate: () => console.log('update stock'),
      formattingFn,
    });

    useEffect(() => {
      if (stock || stock === 0) {
        setCountStock(null);
        updateStockCounter({ stock, limit, productionRate, withReset: true });
      }
    }, [stock, localStockUpdatedAt, JSON.stringify(boosts)]);

    return (
      <div
        style={{ visibility: isStockAnimationStart ? 'hidden' : 'visible' }}
        ref={mergeRefs(ref, countUpRef)}
      ></div>
    );
  },
);

export { Stock };
